import React from 'react';
import { Box, Popover, Typography } from '@mui/material';

import { SLIDE_TEMPLATES } from '../slideTemplates';

import type { SlideTemplate } from '@/shared/types/slide';

// ---------------------------------------------------------------------------
// SlideLayoutPicker
//
// A 2×2 popover grid showing the four layout templates. Selecting one
// calls `onSelect` with the template type.
// ---------------------------------------------------------------------------

interface SlideLayoutPickerProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onSelect: (template: SlideTemplate) => void;
}

/** Basic visual indicator for each template layout. */
const TEMPLATE_THUMBNAILS: Record<SlideTemplate, React.ReactNode> = {
  BLANK: (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        bgcolor: '#fff',
        border: '1px solid #ddd',
        borderRadius: 0.5,
      }}
    />
  ),
  TITLE: (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        bgcolor: '#fff',
        border: '1px solid #ddd',
        borderRadius: 0.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box sx={{ width: '60%', height: 6, bgcolor: '#999', borderRadius: 1 }} />
    </Box>
  ),
  TITLE_AND_BODY: (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        bgcolor: '#fff',
        border: '1px solid #ddd',
        borderRadius: 0.5,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5,
      }}
    >
      <Box sx={{ width: '60%', height: 4, bgcolor: '#999', borderRadius: 1 }} />
      <Box sx={{ width: '70%', height: 12, bgcolor: '#ddd', borderRadius: 1 }} />
    </Box>
  ),
  TITLE_AND_TWO_COLUMNS: (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        bgcolor: '#fff',
        border: '1px solid #ddd',
        borderRadius: 0.5,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5,
      }}
    >
      <Box sx={{ width: '60%', height: 4, bgcolor: '#999', borderRadius: 1 }} />
      <Box sx={{ display: 'flex', gap: 0.5, width: '70%' }}>
        <Box sx={{ flex: 1, height: 12, bgcolor: '#ddd', borderRadius: 1 }} />
        <Box sx={{ flex: 1, height: 12, bgcolor: '#ddd', borderRadius: 1 }} />
      </Box>
    </Box>
  ),
};

const SlideLayoutPicker: React.FC<SlideLayoutPickerProps> = ({
  anchorEl,
  open,
  onClose,
  onSelect,
}) => (
  <Popover
    open={open}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
  >
    <Box sx={{ p: 1.5, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, width: 260 }}>
      {SLIDE_TEMPLATES.map((info) => (
        <Box
          key={info.template}
          onClick={() => onSelect(info.template)}
          sx={{
            cursor: 'pointer',
            '&:hover': { outline: '2px solid', outlineColor: 'primary.main', borderRadius: 1 },
            p: 0.5,
          }}
        >
          <Box sx={{ width: '100%', aspectRatio: '16/9', mb: 0.5 }}>
            {TEMPLATE_THUMBNAILS[info.template]}
          </Box>
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center' }}>
            {info.name}
          </Typography>
        </Box>
      ))}
    </Box>
  </Popover>
);

export default SlideLayoutPicker;
